import AddressSearchStatus from "../components/Address/AddressSearchStatus";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import useMarketPlacesAutocomplete from "../hooks/useMarketPlacesAutocomplete";
import { GoogleMap, Polyline, InfoWindow } from "@react-google-maps/api";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import { debounce, isEqual } from "lodash";
import axios from "axios";

// Material-UI Icons
// import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlagIcon from "@mui/icons-material/Flag";
import StraightenIcon from "@mui/icons-material/Straighten";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InventoryIcon from "@mui/icons-material/Inventory";
import RouteIcon from "@mui/icons-material/Route";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PaymentIcon from "@mui/icons-material/Payment";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import MoneyIcon from "@mui/icons-material/Money";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import Layout from "../components/Global/Layout";
import { mobile } from "../responsive";
import {
  updatePackageDetails,
  setCalculatedDistance,
  resetPackageState,
  setLastPriceCalculationParams,
  calculatePackagePrice,
  createPackageOrder,
} from "../redux/packageSlice";
import {
  payAirtelMoneyCheckout,
  payMoMoCheckout,
} from "../redux/createOrderAction.js";
import { followCardRedirect } from "../utils/cardPayment";
import useCheckoutDeliverySchedule from "../hooks/useCheckoutDeliverySchedule";
import DeliveryTimePicker from "../components/DeliveryTimePicker";
import { toast } from "react-toastify";
import mapStyles from "./Shipping/mapStyles";
import {
  COUNTRY_NAME,
  MAP_CENTER,
  formatCurrency,
  normalizeMarketLatLng,
  isFwMomoMethod,
  fwNetworkFor,
} from "../config/market";
import {
  geocodeAddress,
  getPlaceSummary,
  latLngFromGeocode,
} from "../utils/googlePlaces";
import reportClientError from "../utils/clientTelemetry";
import GoogleMapBoundary from "../components/Maps/GoogleMapBoundary";
import MapPin from "../components/Maps/MapPin";

// OpenRouteService API key (you may need to add this to your .env)
const OPENROUTE_API_KEY = process.env.REACT_APP_OPENROUTE_API_KEY || "";

// Map configuration
// The map fills whatever height MapFrame gives it (the card's remaining
// height on desktop, a fixed 320px on phones), so the page never needs to
// scroll to reach the form beside it.
const mapContainerStyle = {
  width: "100%",
  height: "100%",
  flex: "1 1 auto",
  minHeight: 0,
  borderRadius: "12px",
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: mapStyles,
};

const AVERAGE_DELIVERY_SPEED_KMH = 25;

// Haversine distance calculation
const calculateHaversineDistance = (origin, destination) => {
  const R = 6371;
  const dLat = ((destination.lat - origin.lat) * Math.PI) / 180;
  const dLon = ((destination.lng - origin.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((origin.lat * Math.PI) / 180) *
      Math.cos((destination.lat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1.3; // Multiply by 1.3 to approximate road distance
};

/**
 * Does this method have to be COLLECTED before the package moves?
 *
 * COD is the only one that does not: `packageController.createPackageOrder`
 * stores a COD package as `paymentStatus: "COD"`, which `paymentGate` accepts,
 * so a rider can be assigned and the fee is taken at the door. EVERY other
 * method is stored `PENDING`, and `paymentGate` refuses PENDING on both the
 * rider-assignment and the 3PL-assignment routes — so an uncollected package
 * order is not merely unpaid, it can never be dispatched at all.
 *
 * That is why this page now charges. Until it did, picking mobile money here
 * created an order that no rider could ever be given, under a button that said
 * "Confirm & Pay" and a spinner that said "Creating Order...".
 */
const requiresCollection = (method) => Boolean(method) && method !== "COD";

/** Loose, market-agnostic sanity check; the server normalises and has the last word. */
const payerPhoneDigits = (value) => String(value || "").replace(/\D/g, "");
const isPlausiblePayerPhone = (value) => {
  const digits = payerPhoneDigits(value);
  return digits.length >= 9 && digits.length <= 15;
};

const CreatePackagePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const mapRef = useRef(null);
  const currentUser = useSelector((state) => state.user.currentUser);

  const {
    packageDetails,
    price,
    calculatedDistance,
    status,
    lastPriceCalculationParams,
  } = useSelector((state) => state.package);

  // Local state
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [estimatedDuration, setEstimatedDuration] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  // A parcel is delivered by Lusuku itself, so its windows come from Lusuku's
  // own hours (slot vendorType "inhouse"), not a seller's. Same picker, same
  // 45 minute freshness check, same dialog when the server refuses a window.
  const deliveryTime = useCheckoutDeliverySchedule({ flow: "inhouse" });
  // The number that will actually be debited. It is NOT the sender's or the
  // recipient's: whoever is standing here paying may be neither, and this page
  // previously collected no payable number at all, which is one reason nothing
  // could have been charged even if the call had existed.
  const [payerPhone, setPayerPhone] = useState("");
  const [payerPhoneError, setPayerPhoneError] = useState("");
  // The server's price, once it has told us its figure differs from the quote
  // this page displayed. Null until that happens. Sending it back as the next
  // `quotedPrice` is how the customer CONFIRMS the new number — the server
  // will not book at a price the customer was never shown.
  const [repricedTo, setRepricedTo] = useState(null);
  const [senderPhone, setSenderPhone] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  // Places autocomplete for pickup
  const pickupAutocomplete = useMarketPlacesAutocomplete({ debounce: 300 });

  // Places autocomplete for delivery
  const deliveryAutocomplete = useMarketPlacesAutocomplete({ debounce: 300 });

  useEffect(() => {
    if (!pickupAutocomplete.error && !deliveryAutocomplete.error) return;
    setFormErrors((current) => ({
      ...current,
      ...(pickupAutocomplete.error
        ? { pickupAddress: "Pickup address search is temporarily unavailable." }
        : {}),
      ...(deliveryAutocomplete.error
        ? {
            deliveryAddress:
              "Delivery address search is temporarily unavailable.",
          }
        : {}),
    }));
  }, [deliveryAutocomplete.error, pickupAutocomplete.error]);

  // Reset state on mount
  useEffect(() => {
    dispatch(resetPackageState());
  }, [dispatch]);

  // Generate a curved path between two points (Bezier curve approximation)
  const generateCurvedPath = useCallback(
    (origin, destination, numPoints = 20) => {
      const points = [];

      // Calculate midpoint with offset for curve
      const midLat = (origin.lat + destination.lat) / 2;
      const midLng = (origin.lng + destination.lng) / 2;

      // Calculate perpendicular offset for the control point
      const dx = destination.lng - origin.lng;
      const dy = destination.lat - origin.lat;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Offset the control point perpendicular to the line (creates the curve)
      const offsetAmount = distance * 0.15;
      const controlPoint = {
        lat: midLat + (dx / distance) * offsetAmount * 0.5,
        lng: midLng - (dy / distance) * offsetAmount,
      };

      // Generate points along the quadratic Bezier curve
      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;
        const lat =
          Math.pow(1 - t, 2) * origin.lat +
          2 * (1 - t) * t * controlPoint.lat +
          Math.pow(t, 2) * destination.lat;
        const lng =
          Math.pow(1 - t, 2) * origin.lng +
          2 * (1 - t) * t * controlPoint.lng +
          Math.pow(t, 2) * destination.lng;
        points.push({ lat, lng });
      }

      return points;
    },
    [],
  );

  // Fetch route coordinates from OpenRouteService for fluid route display
  const fetchRouteCoordinates = useCallback(
    async (origin, destination) => {
      // If no API key, fall back to curved line approximation
      if (!OPENROUTE_API_KEY) {
        const curvedPath = generateCurvedPath(origin, destination, 20);
        setRouteCoordinates(curvedPath);
        return;
      }

      try {
        const response = await axios.get(
          `https://api.openrouteservice.org/v2/directions/driving-car`,
          {
            params: {
              api_key: OPENROUTE_API_KEY,
              start: `${origin.lng},${origin.lat}`,
              end: `${destination.lng},${destination.lat}`,
            },
          },
        );

        if (response.data?.features?.[0]?.geometry?.coordinates) {
          const route = response.data.features[0].geometry.coordinates;
          setRouteCoordinates(
            route.map((coord) => ({ lat: coord[1], lng: coord[0] })),
          );
        }
      } catch (error) {
        console.error("Error fetching route:", error);
        const curvedPath = generateCurvedPath(origin, destination, 20);
        setRouteCoordinates(curvedPath);
      }
    },
    [generateCurvedPath],
  );

  // Calculate distance and route when both addresses are set
  useEffect(() => {
    if (packageDetails.pickupAddress && packageDetails.deliveryAddress) {
      const origin = {
        lat: packageDetails.pickupAddress.latitude,
        lng: packageDetails.pickupAddress.longitude,
      };
      const destination = {
        lat: packageDetails.deliveryAddress.latitude,
        lng: packageDetails.deliveryAddress.longitude,
      };

      // Calculate distance using Haversine formula
      const distanceInKm = calculateHaversineDistance(origin, destination);
      const durationInMinutes = Math.ceil(
        (distanceInKm / AVERAGE_DELIVERY_SPEED_KMH) * 60,
      );

      dispatch(setCalculatedDistance({ distanceInKm }));
      setEstimatedDuration(durationInMinutes);

      // Fetch actual road route coordinates
      fetchRouteCoordinates(origin, destination);

      // Fit map to show both markers
      if (mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(origin);
        bounds.extend(destination);
        mapRef.current.fitBounds(bounds, { padding: 80 });
      }
    }
  }, [
    packageDetails.pickupAddress,
    packageDetails.deliveryAddress,
    dispatch,
    fetchRouteCoordinates,
  ]);

  // Calculate price when relevant details change
  const calculatePrice = useCallback(
    async (details, distance) => {
      if (
        details.pickupAddress &&
        details.deliveryAddress &&
        details.weight &&
        distance
      ) {
        const currentParams = {
          pickupAddress: details.pickupAddress,
          deliveryAddress: details.deliveryAddress,
          weight: details.weight,
          isFragile: details.isFragile,
          distance: distance,
        };

        if (!isEqual(currentParams, lastPriceCalculationParams)) {
          setIsCalculatingPrice(true);
          try {
            await dispatch(
              calculatePackagePrice({
                packageDetails: details,
                calculatedDistance: distance,
              }),
            ).unwrap();
            dispatch(setLastPriceCalculationParams(currentParams));
          } catch (error) {
            console.error("Error calculating price:", error);
          } finally {
            setIsCalculatingPrice(false);
          }
        }
      }
    },
    [dispatch, lastPriceCalculationParams],
  );

  const debouncedCalculatePrice = useMemo(
    () => debounce(calculatePrice, 500),
    [calculatePrice],
  );

  useEffect(() => {
    if (
      packageDetails.pickupAddress &&
      packageDetails.deliveryAddress &&
      packageDetails.weight &&
      calculatedDistance
    ) {
      debouncedCalculatePrice(packageDetails, calculatedDistance);
    }
  }, [packageDetails, calculatedDistance, debouncedCalculatePrice]);

  // Handle pickup address selection
  const handlePickupSelect = async (address) => {
    pickupAutocomplete.setValue(address, false);
    pickupAutocomplete.clearSuggestions();

    try {
      const results = await geocodeAddress(address);
      const raw = latLngFromGeocode(results[0]);
      const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
      if (!coordinates)
        throw new Error("The pickup address is outside this delivery market");
      const details = await getPlaceSummary(results[0].place_id, address);

      dispatch(
        updatePackageDetails({
          pickupAddress: {
            address: address,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            placeId: results[0].place_id,
            name: details?.name || address,
          },
        }),
      );
      setFormErrors((current) => ({ ...current, pickupAddress: "" }));
    } catch (error) {
      reportClientError(error, { area: "package_pickup_geocode" });
      setFormErrors((current) => ({
        ...current,
        pickupAddress:
          "We couldn’t pin that pickup location. Choose another suggestion.",
      }));
    }
  };

  // Handle delivery address selection
  const handleDeliverySelect = async (address) => {
    deliveryAutocomplete.setValue(address, false);
    deliveryAutocomplete.clearSuggestions();

    try {
      const results = await geocodeAddress(address);
      const raw = latLngFromGeocode(results[0]);
      const coordinates = normalizeMarketLatLng(raw.lat, raw.lng);
      if (!coordinates)
        throw new Error("The delivery address is outside this delivery market");
      const details = await getPlaceSummary(results[0].place_id, address);

      dispatch(
        updatePackageDetails({
          deliveryAddress: {
            address: address,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            placeId: results[0].place_id,
            name: details?.name || address,
          },
        }),
      );
      setFormErrors((current) => ({ ...current, deliveryAddress: "" }));
    } catch (error) {
      reportClientError(error, { area: "package_delivery_geocode" });
      setFormErrors((current) => ({
        ...current,
        deliveryAddress:
          "We couldn’t pin that delivery location. Choose another suggestion.",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!packageDetails.pickupAddress) {
      errors.pickupAddress = "Pickup address is required";
    }
    if (!packageDetails.deliveryAddress) {
      errors.deliveryAddress = "Delivery address is required";
    }
    if (!packageDetails.weight || parseFloat(packageDetails.weight) <= 0) {
      errors.weight = "Valid package weight is required";
    }
    if (!packageDetails.description) {
      errors.description = "Package description is required";
    }
    if (!senderPhone || senderPhone.length < 10) {
      errors.senderPhone = "Valid sender phone is required (min 10 digits)";
    }
    if (!recipientPhone || recipientPhone.length < 10) {
      errors.recipientPhone =
        "Valid recipient phone is required (min 10 digits)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!calculatedDistance || !price) {
      alert("Please wait for price calculation to complete");
      return;
    }

    setShowPaymentModal(true);
  };

  /** Order created, nothing more to collect — send them to their orders. */
  const finishWithoutCharge = () => {
    setShowPaymentModal(false);
    history.push("/my_profile/my_orders");
  };

  /**
   * Create the package order, then COLLECT for it.
   *
   * Order first, charge second, bound by orderId — the same shape every other
   * Lusuku checkout uses (see PlaceOrderScreen). A payment call never creates
   * an order, and the order is never presented as paid until a rail confirms
   * it.
   *
   * THE AMOUNT IS THE SERVER'S, NOT THIS PAGE'S. `createPackageOrder`
   * deliberately IGNORES the `price` sent up and recomputes the fee from the
   * weight tiers and the road distance IT measured, so the figure shown in
   * this modal can legitimately differ from what the order actually costs.
   * Charging the modal's figure would be rejected outright by the pre-charge
   * amount guard, which compares against the stored order and refuses a
   * disagreement over 1. So the charge reads `totalPrice` back off the
   * created order.
   */
  const handlePaymentSubmit = async () => {
    if (!paymentMethod) {
      alert("Please select a payment method");
      return;
    }

    if (!calculatedDistance) {
      alert(
        "Distance is missing. Please confirm pickup and delivery locations.",
      );
      return;
    }

    if (
      requiresCollection(paymentMethod) &&
      !isPlausiblePayerPhone(payerPhone)
    ) {
      setPayerPhoneError(
        "Enter the mobile money number that will approve the payment.",
      );
      return;
    }
    setPayerPhoneError("");

    setSubmitting(true);
    try {
      const latestPrice = await dispatch(
        calculatePackagePrice({
          packageDetails,
          calculatedDistance,
        }),
      ).unwrap();

      const finalPrice = Number(latestPrice ?? price);

      if (!Number.isFinite(finalPrice) || finalPrice <= 0) {
        alert("Unable to calculate package price. Please try again.");
        return;
      }

      const packageData = {
        ...packageDetails,
        userId: currentUser._id,
        price: finalPrice,
        paymentMethod: paymentMethod,
        senderPhone: senderPhone,
        recipientPhone: recipientPhone,
        calculatedDistance: calculatedDistance,
        customerNumber: recipientPhone,
        // The number to debit, kept distinct from the sender's and the
        // recipient's. The server records it against the order so
        // reconciliation can see which handset was asked.
        payerPhone: requiresCollection(paymentMethod) ? payerPhone.trim() : "",
        // WHAT THIS PAGE ACTUALLY SHOWED THE CUSTOMER. The server
        // measures the road distance itself and re-prices from that, so
        // its figure can legitimately differ from this quote. Sending
        // ours means a difference comes back as a question instead of
        // silently becoming the amount we charge.
        quotedPrice: repricedTo ?? finalPrice,
        orderSrc: "Web",
      };

      // One guarded path for the create: the picker stops the submit and
      // opens its dialog when the window is missing or stale, and reads
      // DELIVERY_SLOT_INVALID / SCHEDULED_ORDERS_ONLY off a refusal.
      const outcome = await deliveryTime.submitRequest((deliverySchedule) =>
        dispatch(
          createPackageOrder({ ...packageData, deliverySchedule }),
        ).unwrap(),
      );
      if (outcome.stopped || outcome.handled) return;
      // Anything else keeps the behaviour it had before: the catch below
      // handles PACKAGE_PRICE_CHANGED and shows the server's reason.
      if (!outcome.ok) throw outcome.error;
      const createdOrder = outcome.data;

      // Cash on delivery: the rider collects. Nothing to charge.
      if (!requiresCollection(paymentMethod)) {
        finishWithoutCharge();
        return;
      }

      const orderId = createdOrder?._id;
      const chargeAmount = Number(createdOrder?.totalPrice);
      if (!orderId || !Number.isFinite(chargeAmount) || chargeAmount <= 0) {
        // The order exists but we cannot address a charge to it. Say so
        // rather than implying it is paid.
        toast.error(
          "Your package order was created, but we couldn't start the payment. Open it in My Orders to pay.",
          { autoClose: false },
        );
        finishWithoutCharge();
        return;
      }

      // ── The Flutterwave mobile-money rail ────────────────────────────
      // Airtel everywhere, and "MOMO" too on markets whose mobile money
      // rides Flutterwave (Zambia) — the direct MTN rail below is Uganda
      // only and refuses anything else outright.
      if (paymentMethod === "Airtel" || isFwMomoMethod(paymentMethod)) {
        const action = await dispatch(
          payAirtelMoneyCheckout({
            mobile: payerPhone.trim(),
            totalPrice: chargeAmount,
            client_id: currentUser._id,
            email: currentUser?.email || undefined,
            uniqueTxRef: `${currentUser._id}-${orderId}`,
            network: fwNetworkFor(paymentMethod) || paymentMethod,
            redirect_url: `${window.location.origin}/airtelPay`,
            orderId,
          }),
        );

        if (!action.error && action.payload?.data?.meta?.authorization) {
          // Uganda mobile money authorises on Flutterwave's hosted
          // page: the PIN prompt does not arrive until the customer
          // gets there. Dropping this link is what leaves a customer
          // waiting for a prompt that is never coming.
          setShowPaymentModal(false);
          followCardRedirect(action.payload.redirectUrl);
          return;
        }

        if (!action.error) {
          toast.error(
            "We couldn't start the mobile money payment. Your package order was created but has NOT been paid \u2014 open it in My Orders to try again.",
            { autoClose: false },
          );
        }
        finishWithoutCharge();
        return;
      }

      // ── The direct MTN Uganda rail ───────────────────────────────────
      // No redirect: MTN pushes the approval prompt to the handset itself.
      const action = await dispatch(
        payMoMoCheckout({
          mobile: payerPhone.trim(),
          totalPrice: chargeAmount,
          // The handler reads client_id from the BODY, not the path.
          client_id: currentUser._id,
          orderId,
        }),
      );

      if (action.error) {
        toast.error(
          "We couldn't start the MTN MoMo payment. Your package order was created but has NOT been paid \u2014 open it in My Orders to try again.",
          { autoClose: false },
        );
      } else {
        toast.info(
          `Approve the payment on ${payerPhone.trim()}. Your package is booked once it goes through.`,
          { autoClose: 8000 },
        );
      }
      finishWithoutCharge();
    } catch (error) {
      // The server priced this differently from the quote we displayed.
      // That is not a failure — it is a number the customer has not
      // agreed to yet. Show it and let them confirm; the next submit
      // sends it back as the agreed price.
      if (
        error?.code === "PACKAGE_PRICE_CHANGED" &&
        Number.isFinite(Number(error.price))
      ) {
        setRepricedTo(Number(error.price));
        return;
      }
      console.error("Error creating package order:", error);
      const errorMessage =
        (typeof error === "string" && error) ||
        error?.message ||
        error?.error ||
        "Failed to create package order. Please try again.";
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Clear pickup address
  const clearPickupAddress = () => {
    pickupAutocomplete.setValue("");
    dispatch(updatePackageDetails({ pickupAddress: null }));
    setRouteCoordinates([]);
    setEstimatedDuration(null);
  };

  // Clear delivery address
  const clearDeliveryAddress = () => {
    deliveryAutocomplete.setValue("");
    dispatch(updatePackageDetails({ deliveryAddress: null }));
    setRouteCoordinates([]);
    setEstimatedDuration(null);
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return "--";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Get map center
  const mapCenter = packageDetails.pickupAddress
    ? {
        lat: packageDetails.pickupAddress.latitude,
        lng: packageDetails.pickupAddress.longitude,
      }
    : packageDetails.deliveryAddress
      ? {
          lat: packageDetails.deliveryAddress.latitude,
          lng: packageDetails.deliveryAddress.longitude,
        }
      : MAP_CENTER;

  const isRouteStepComplete = Boolean(
    packageDetails.pickupAddress && packageDetails.deliveryAddress,
  );

  const goToPackageStep = () => {
    const stepErrors = {};

    if (!packageDetails.pickupAddress) {
      stepErrors.pickupAddress = "Pickup address is required";
    }

    if (!packageDetails.deliveryAddress) {
      stepErrors.deliveryAddress = "Delivery address is required";
    }

    if (Object.keys(stepErrors).length > 0) {
      setFormErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }

    setActiveStep(2);
  };

  return (
    <Layout>
      <PageContainer>
        <PageWrapper>
          <PageHeader>
            <HeaderIconWrapper>
              <span
                style={{ fontSize: "63px", lineHeight: 1 }}
                aria-hidden="true"
              >
                📦
              </span>
            </HeaderIconWrapper>
            <HeaderTitle>Send a Package</HeaderTitle>
            <HeaderSubtitle>
              Fast and reliable delivery across {COUNTRY_NAME}!
            </HeaderSubtitle>
          </PageHeader>

          <StepTabs>
            <StepTab
              $active={activeStep === 1}
              $done={isRouteStepComplete}
              onClick={() => setActiveStep(1)}
            >
              <StepNumber
                $active={activeStep === 1}
                $done={isRouteStepComplete}
              >
                1
              </StepNumber>
              Route & Locations
            </StepTab>
            <StepTab
              $active={activeStep === 2}
              $done={activeStep === 2}
              onClick={goToPackageStep}
            >
              <StepNumber $active={activeStep === 2} $done={activeStep === 2}>
                2
              </StepNumber>
              Package & Contact
            </StepTab>
          </StepTabs>

          <ContentGrid>
            {/* Map Section */}
            <MapSection>
              <MapFrame>
                <GoogleMapBoundary height="100%">
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={13}
                    center={mapCenter}
                    options={mapOptions}
                    onLoad={(map) => (mapRef.current = map)}
                  >
                    {/* Pickup Marker */}
                    {packageDetails.pickupAddress && (
                      <MapPin
                        position={{
                          lat: packageDetails.pickupAddress.latitude,
                          lng: packageDetails.pickupAddress.longitude,
                        }}
                        color="#4CAF50"
                        label="Pickup location"
                        onClick={() => setActiveInfoWindow("pickup")}
                      />
                    )}
                    {packageDetails.pickupAddress &&
                      activeInfoWindow === "pickup" && (
                        <InfoWindow
                          position={{
                            lat: packageDetails.pickupAddress.latitude,
                            lng: packageDetails.pickupAddress.longitude,
                          }}
                          onCloseClick={() => setActiveInfoWindow(null)}
                        >
                          <InfoContent>
                            <InfoTitle>
                              <LocationOnIcon
                                style={{
                                  fontSize: 16,
                                  color: "#4CAF50",
                                  verticalAlign: "middle",
                                  marginRight: 4,
                                }}
                              />
                              Pickup Location
                            </InfoTitle>
                            <InfoText>
                              {packageDetails.pickupAddress.address}
                            </InfoText>
                          </InfoContent>
                        </InfoWindow>
                      )}

                    {/* Delivery Marker */}
                    {packageDetails.deliveryAddress && (
                      <MapPin
                        position={{
                          lat: packageDetails.deliveryAddress.latitude,
                          lng: packageDetails.deliveryAddress.longitude,
                        }}
                        color="#f44336"
                        label="Delivery location"
                        onClick={() => setActiveInfoWindow("delivery")}
                      />
                    )}
                    {packageDetails.deliveryAddress &&
                      activeInfoWindow === "delivery" && (
                        <InfoWindow
                          position={{
                            lat: packageDetails.deliveryAddress.latitude,
                            lng: packageDetails.deliveryAddress.longitude,
                          }}
                          onCloseClick={() => setActiveInfoWindow(null)}
                        >
                          <InfoContent>
                            <InfoTitle>
                              <FlagIcon
                                style={{
                                  fontSize: 16,
                                  color: "#f44336",
                                  verticalAlign: "middle",
                                  marginRight: 4,
                                }}
                              />
                              Delivery Location
                            </InfoTitle>
                            <InfoText>
                              {packageDetails.deliveryAddress.address}
                            </InfoText>
                          </InfoContent>
                        </InfoWindow>
                      )}

                    {/* Route Line */}
                    {routeCoordinates.length >= 2 && (
                      <Polyline
                        path={routeCoordinates}
                        options={{
                          strokeColor: "#4CAF50",
                          strokeOpacity: 0.9,
                          strokeWeight: 5,
                          geodesic: true,
                        }}
                      />
                    )}
                  </GoogleMap>
                </GoogleMapBoundary>
              </MapFrame>

              {/* Route Info */}
              {(calculatedDistance || estimatedDuration || price) && (
                <RouteInfoCard>
                  <RouteInfoItem>
                    <RouteInfoIconWrapper>
                      <StraightenIcon style={{ fontSize: 24, color: "#fff" }} />
                    </RouteInfoIconWrapper>
                    <RouteInfoValue>
                      {calculatedDistance
                        ? `${calculatedDistance.toFixed(1)} km`
                        : "--"}
                    </RouteInfoValue>
                    <RouteInfoLabel>Distance</RouteInfoLabel>
                  </RouteInfoItem>
                  <RouteInfoDivider />
                  <RouteInfoItem>
                    <RouteInfoIconWrapper>
                      <AccessTimeIcon style={{ fontSize: 24, color: "#fff" }} />
                    </RouteInfoIconWrapper>
                    <RouteInfoValue>
                      {formatDuration(estimatedDuration)}
                    </RouteInfoValue>
                    <RouteInfoLabel>Est. Time</RouteInfoLabel>
                  </RouteInfoItem>
                  <RouteInfoDivider />
                  <RouteInfoItem>
                    <RouteInfoIconWrapper>
                      <AttachMoneyIcon
                        style={{ fontSize: 24, color: "#fff" }}
                      />
                    </RouteInfoIconWrapper>
                    <RouteInfoValue>
                      {isCalculatingPrice
                        ? "..."
                        : price
                          ? formatCurrency(price)
                          : "--"}
                    </RouteInfoValue>
                    <RouteInfoLabel>Est. Price</RouteInfoLabel>
                  </RouteInfoItem>
                </RouteInfoCard>
              )}
            </MapSection>

            {/* Form Section */}
            <FormSection>
              {activeStep === 1 && (
                <>
                  <SectionTitle>
                    <RouteIcon
                      style={{
                        fontSize: 20,
                        color: "#4CAF50",
                        marginRight: 8,
                        verticalAlign: "middle",
                      }}
                    />
                    Route Details
                  </SectionTitle>

                  <AddressCard>
                    <AddressHeader>
                      <AddressIconWrapper $color="#4CAF50">
                        <LocationOnIcon
                          style={{ fontSize: 20, color: "#4CAF50" }}
                        />
                      </AddressIconWrapper>
                      <AddressLabel>Pickup Location</AddressLabel>
                      {packageDetails.pickupAddress && (
                        <ClearButton onClick={clearPickupAddress}>
                          <CloseIcon style={{ fontSize: 14 }} />
                        </ClearButton>
                      )}
                    </AddressHeader>
                    {packageDetails.pickupAddress ? (
                      <SelectedAddress>
                        {packageDetails.pickupAddress.address}
                      </SelectedAddress>
                    ) : (
                      <Combobox onSelect={handlePickupSelect}>
                        <AddressSearchStatus loading={pickupAutocomplete.suggestions.loading} ready={pickupAutocomplete.ready} status={pickupAutocomplete.suggestions.status} count={pickupAutocomplete.suggestions.data.length} error={pickupAutocomplete.error} />
                        <StyledComboboxInput
                          value={pickupAutocomplete.value}
                          onChange={(e) => {
                            pickupAutocomplete.setValue(e.target.value);
                            setFormErrors((current) => ({
                              ...current,
                              pickupAddress: "",
                            }));
                          }}
                          disabled={!pickupAutocomplete.ready}
                          placeholder={
                            pickupAutocomplete.ready
                              ? "Where should we pick up the package?"
                              : "Loading address search…"
                          }
                          $hasError={formErrors.pickupAddress}
                          autoComplete="off"
                        />
                        <ComboboxPopover>
                          <StyledComboboxList>
                            {pickupAutocomplete.suggestions.data.map(
                              (suggestion) => (
                                <ComboboxOption
                                  key={suggestion.place_id}
                                  value={suggestion.description}
                                />
                              ),
                            )}
                          </StyledComboboxList>
                        </ComboboxPopover>
                      </Combobox>
                    )}
                    {formErrors.pickupAddress && (
                      <ErrorText>{formErrors.pickupAddress}</ErrorText>
                    )}
                  </AddressCard>

                  <AddressConnector>
                    <ConnectorLine />
                    <ConnectorDot>
                      <ArrowDownwardIcon
                        style={{ fontSize: 16, color: "#4CAF50" }}
                      />
                    </ConnectorDot>
                    <ConnectorLine />
                  </AddressConnector>

                  <AddressCard>
                    <AddressHeader>
                      <AddressIconWrapper $color="#f44336">
                        <FlagIcon style={{ fontSize: 20, color: "#f44336" }} />
                      </AddressIconWrapper>
                      <AddressLabel>Delivery Location</AddressLabel>
                      {packageDetails.deliveryAddress && (
                        <ClearButton onClick={clearDeliveryAddress}>
                          <CloseIcon style={{ fontSize: 14 }} />
                        </ClearButton>
                      )}
                    </AddressHeader>
                    {packageDetails.deliveryAddress ? (
                      <SelectedAddress>
                        {packageDetails.deliveryAddress.address}
                      </SelectedAddress>
                    ) : (
                      <Combobox onSelect={handleDeliverySelect}>
                        <AddressSearchStatus loading={deliveryAutocomplete.suggestions.loading} ready={deliveryAutocomplete.ready} status={deliveryAutocomplete.suggestions.status} count={deliveryAutocomplete.suggestions.data.length} error={deliveryAutocomplete.error} />
                        <StyledComboboxInput
                          value={deliveryAutocomplete.value}
                          onChange={(e) => {
                            deliveryAutocomplete.setValue(e.target.value);
                            setFormErrors((current) => ({
                              ...current,
                              deliveryAddress: "",
                            }));
                          }}
                          disabled={!deliveryAutocomplete.ready}
                          placeholder={
                            deliveryAutocomplete.ready
                              ? "Where should we deliver?"
                              : "Loading address search…"
                          }
                          $hasError={formErrors.deliveryAddress}
                          autoComplete="off"
                        />
                        <ComboboxPopover>
                          <StyledComboboxList>
                            {deliveryAutocomplete.suggestions.data.map(
                              (suggestion) => (
                                <ComboboxOption
                                  key={suggestion.place_id}
                                  value={suggestion.description}
                                />
                              ),
                            )}
                          </StyledComboboxList>
                        </ComboboxPopover>
                      </Combobox>
                    )}
                    {formErrors.deliveryAddress && (
                      <ErrorText>{formErrors.deliveryAddress}</ErrorText>
                    )}
                  </AddressCard>

                  <StepActions>
                    <PrimaryStepButton onClick={goToPackageStep}>
                      Continue to Package Details
                      <ArrowForwardIcon style={{ fontSize: 18 }} />
                    </PrimaryStepButton>
                  </StepActions>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <SectionTitle>
                    <InventoryIcon
                      style={{
                        fontSize: 20,
                        color: "#4CAF50",
                        marginRight: 8,
                        verticalAlign: "middle",
                      }}
                    />
                    Package Details
                  </SectionTitle>

                  <FormRow>
                    <FormGroup style={{ flex: 2 }}>
                      <FormLabel>Weight (kg) *</FormLabel>
                      <FormInput
                        type="number"
                        placeholder="Enter weight"
                        value={packageDetails.weight}
                        onChange={(e) =>
                          dispatch(
                            updatePackageDetails({ weight: e.target.value }),
                          )
                        }
                        $hasError={formErrors.weight}
                      />
                      {formErrors.weight && (
                        <ErrorText>{formErrors.weight}</ErrorText>
                      )}
                    </FormGroup>

                    <FragileToggle>
                      <FragileLabel>
                        <NotificationsActiveIcon
                          style={{
                            fontSize: 20,
                            color: packageDetails.isFragile
                              ? "#ff9800"
                              : "#999",
                            marginRight: 8,
                          }}
                        />
                        Fragile
                      </FragileLabel>
                      <ToggleSwitch
                        $active={packageDetails.isFragile}
                        onClick={() =>
                          dispatch(
                            updatePackageDetails({
                              isFragile: !packageDetails.isFragile,
                            }),
                          )
                        }
                      >
                        <ToggleThumb $active={packageDetails.isFragile} />
                      </ToggleSwitch>
                    </FragileToggle>
                  </FormRow>

                  <FormGroup>
                    <FormLabel>Package Description *</FormLabel>
                    <FormInput
                      as="textarea"
                      rows={3}
                      placeholder="What are you sending? (e.g., Documents, Electronics)"
                      value={packageDetails.description}
                      onChange={(e) =>
                        dispatch(
                          updatePackageDetails({ description: e.target.value }),
                        )
                      }
                      $hasError={formErrors.description}
                    />
                    {formErrors.description && (
                      <ErrorText>{formErrors.description}</ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <PhoneAndroidIcon
                        style={{
                          fontSize: 16,
                          color: "#4CAF50",
                          marginRight: 4,
                          verticalAlign: "middle",
                        }}
                      />{" "}
                      Sender's Phone (for pickup) *
                    </FormLabel>
                    <FormInput
                      type="tel"
                      placeholder="e.g., 0700123456"
                      value={senderPhone}
                      onChange={(e) => setSenderPhone(e.target.value)}
                      $hasError={formErrors.senderPhone}
                    />
                    {formErrors.senderPhone && (
                      <ErrorText>{formErrors.senderPhone}</ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      <PhoneAndroidIcon
                        style={{
                          fontSize: 16,
                          color: "#4CAF50",
                          marginRight: 4,
                          verticalAlign: "middle",
                        }}
                      />{" "}
                      Recipient's Phone (for delivery) *
                    </FormLabel>
                    <FormInput
                      type="tel"
                      placeholder="e.g., 0700123456"
                      value={recipientPhone}
                      onChange={(e) => setRecipientPhone(e.target.value)}
                      $hasError={formErrors.recipientPhone}
                    />
                    {formErrors.recipientPhone && (
                      <ErrorText>{formErrors.recipientPhone}</ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Special Instructions (Optional)</FormLabel>
                    <FormInput
                      as="textarea"
                      rows={2}
                      placeholder="Any special handling instructions..."
                      value={packageDetails.specialInstructions}
                      onChange={(e) =>
                        dispatch(
                          updatePackageDetails({
                            specialInstructions: e.target.value,
                          }),
                        )
                      }
                    />
                  </FormGroup>

                  <StepActions>
                    <SecondaryStepButton onClick={() => setActiveStep(1)}>
                      <ArrowBackIcon style={{ fontSize: 16 }} />
                      Back to Route Details
                    </SecondaryStepButton>
                    <SubmitButton
                      onClick={handleSubmit}
                      disabled={status === "loading" || isCalculatingPrice}
                    >
                      {status === "loading"
                        ? "Processing..."
                        : "Continue to Payment"}
                    </SubmitButton>
                  </StepActions>
                </>
              )}
            </FormSection>
          </ContentGrid>
        </PageWrapper>

        {/* Payment Modal */}
        {showPaymentModal && (
          <ModalOverlay
            onClick={() => {
              setShowPaymentModal(false);
              setRepricedTo(null);
            }}
          >
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  <PaymentIcon
                    style={{
                      fontSize: 24,
                      color: "#4CAF50",
                      marginRight: 8,
                      verticalAlign: "middle",
                    }}
                  />
                  Payment Details
                </ModalTitle>
                <ModalClose
                  onClick={() => {
                    setShowPaymentModal(false);
                    setRepricedTo(null);
                  }}
                >
                  <CloseIcon style={{ fontSize: 20 }} />
                </ModalClose>
              </ModalHeader>

              <ModalBody>
                <OrderSummary>
                  <SummaryRow>
                    <span>Distance:</span>
                    <span>{calculatedDistance?.toFixed(1)} km</span>
                  </SummaryRow>
                  <SummaryRow>
                    <span>Est. Delivery Time:</span>
                    <span>{formatDuration(estimatedDuration)}</span>
                  </SummaryRow>
                  <SummaryRow $total>
                    <span>Total Price:</span>
                    <span>{formatCurrency(price)}</span>
                  </SummaryRow>
                </OrderSummary>

                <DeliveryTimePicker controller={deliveryTime} />

                <PaymentMethods>
                  <PaymentMethodTitle>Select Payment Method</PaymentMethodTitle>
                  <PaymentOption
                    $selected={paymentMethod === "MOMO"}
                    onClick={() => setPaymentMethod("MOMO")}
                  >
                    <PaymentIconWrapper $color="#ffcc00">
                      <PhoneAndroidIcon
                        style={{ fontSize: 24, color: "#ffcc00" }}
                      />
                    </PaymentIconWrapper>
                    <PaymentName>MTN Mobile Money</PaymentName>
                    {paymentMethod === "MOMO" && (
                      <SelectedPaymentTick>
                        <CheckCircleIcon style={{ fontSize: 20 }} />
                      </SelectedPaymentTick>
                    )}
                  </PaymentOption>
                  <PaymentOption
                    $selected={paymentMethod === "Airtel"}
                    onClick={() => setPaymentMethod("Airtel")}
                  >
                    <PaymentIconWrapper $color="#e4002b">
                      <PhoneAndroidIcon
                        style={{ fontSize: 24, color: "#e4002b" }}
                      />
                    </PaymentIconWrapper>
                    <PaymentName>Airtel Money</PaymentName>
                    {paymentMethod === "Airtel" && (
                      <SelectedPaymentTick>
                        <CheckCircleIcon style={{ fontSize: 20 }} />
                      </SelectedPaymentTick>
                    )}
                  </PaymentOption>
                  <PaymentOption
                    $selected={paymentMethod === "COD"}
                    onClick={() => setPaymentMethod("COD")}
                  >
                    <PaymentIconWrapper $color="#4CAF50">
                      <MoneyIcon style={{ fontSize: 24, color: "#4CAF50" }} />
                    </PaymentIconWrapper>
                    <PaymentName>Cash on Delivery</PaymentName>
                    {paymentMethod === "COD" && (
                      <SelectedPaymentTick>
                        <CheckCircleIcon style={{ fontSize: 20 }} />
                      </SelectedPaymentTick>
                    )}
                  </PaymentOption>
                </PaymentMethods>

                {requiresCollection(paymentMethod) && (
                  <FormGroup style={{ marginTop: 16 }}>
                    <FormLabel>
                      {paymentMethod === "Airtel"
                        ? "Airtel Money number to charge"
                        : "Mobile money number to charge"}
                    </FormLabel>
                    <FormInput
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="07XXXXXXXX"
                      value={payerPhone}
                      $hasError={Boolean(payerPhoneError)}
                      onChange={(e) => {
                        setPayerPhone(e.target.value);
                        if (payerPhoneError) setPayerPhoneError("");
                      }}
                    />
                    {payerPhoneError ? (
                      <ErrorText>{payerPhoneError}</ErrorText>
                    ) : (
                      <ErrorText style={{ color: "#666" }}>
                        The payment is approved on this phone.
                      </ErrorText>
                    )}
                  </FormGroup>
                )}

                {repricedTo !== null && (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 12,
                      borderRadius: 8,
                      background: "#FFF8E1",
                      border: "1px solid #F2C94C",
                      color: "#5D4300",
                      fontSize: 14,
                    }}
                  >
                    <strong>The delivery price has changed.</strong>
                    <div style={{ marginTop: 4 }}>
                      We measured the route and this delivery is{" "}
                      <strong>{formatCurrency(repricedTo)}</strong>, not{" "}
                      {formatCurrency(price)}. Nothing has been booked or
                      charged yet — confirm to continue at the new price.
                    </div>
                  </div>
                )}

                <PayButton onClick={handlePaymentSubmit} disabled={submitting}>
                  {/* The label has to be true. It said "Confirm & Pay" for
                                        every method while nothing was ever charged; COD is
                                        genuinely paid to the rider, so only the collected
                                        methods may promise a payment here. */}
                  {submitting
                    ? requiresCollection(paymentMethod)
                      ? "Starting payment..."
                      : "Booking package..."
                    : repricedTo !== null
                      ? `Confirm ${formatCurrency(repricedTo)}`
                      : requiresCollection(paymentMethod)
                        ? "Confirm & Pay"
                        : "Book package"}
                </PayButton>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export default CreatePackagePage;

// Styled Components
// Desktop: the page is exactly the space under the fixed navbar (Layout's
// MainContent pads the top by --lusuku-nav-h), laid out as a column whose
// header and step tabs take their natural height and whose two cards take
// the rest. Nothing on the page scrolls; a card scrolls inside itself when
// its content is taller than the card. Phones keep a normal scrolling page.
const PageContainer = styled.div`
  height: calc(100vh - var(--lusuku-nav-h, 116px));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 25px 20px 16px;

  ${mobile({
    height: "auto",
    minHeight: "calc(100vh - var(--lusuku-nav-h, 120px))",
    overflow: "visible",
    padding: "20px 10px 10px",
  })}
`;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 12px;
  flex: none;
`;

const StepTabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
  flex: none;
`;

const StepTab = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid
    ${(props) => (props.$active || props.$done ? "#4caf50" : "#d8e0d8")};
  background: ${(props) => (props.$active ? "#eaf7eb" : "#ffffff")};
  color: ${(props) => (props.$active || props.$done ? "#1f5e23" : "#5b635b")};
  font-weight: 600;
  font-size: 14px;
  padding: 12px 10px;
  cursor: pointer;
`;

const StepNumber = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(props) =>
    props.$active || props.$done ? "#4caf50" : "#edf1ed"};
  color: ${(props) => (props.$active || props.$done ? "#fff" : "#6d756d")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const HeaderIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-radius: 50%;
  margin: 0 auto 10px;
  overflow: hidden;

  /* Short desktop windows (a laptop with the dock showing): drop the
     ornament so the cards keep their height. */
  @media only screen and (min-width: 851px) and (max-height: 720px) {
    display: none;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #333;
  margin: 0 0 4px 0;

  ${mobile({ fontSize: "24px" })}
`;

const HeaderSubtitle = styled.p`
  font-size: 15px;
  color: #666;
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  flex: 1;
  min-height: 0;

  ${mobile({
    gridTemplateColumns: "1fr",
    gridTemplateRows: "none",
    flex: "none",
  })}
`;

const MapSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

/* The map's box: whatever height is left in the card after the route
   summary, never less than 240px, and a fixed 320px on phones where the
   page scrolls instead. */
const MapFrame = styled.div`
  flex: 1 1 auto;
  min-height: 240px;
  display: flex;
  flex-direction: column;

  ${mobile({ flex: "none", height: "320px" })}
`;

const FormSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  min-height: 0;
  overflow-y: auto;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const AddressCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
`;

const AddressHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const AddressIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${(props) => props.$color}15;
  border-radius: 8px;
`;

const AddressLabel = styled.span`
  font-weight: 600;
  color: #333;
  flex: 1;
`;

const ClearButton = styled.button`
  background: #ff5252;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f44336;
  }
`;

const SelectedAddress = styled.div`
  padding: 12px;
  background: white;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  border: 1px solid #e0e0e0;
`;

const StyledComboboxInput = styled(ComboboxInput)`
  width: 100%;
  padding: 12px;
  border: 1px solid ${(props) => (props.$hasError ? "#f44336" : "#e0e0e0")};
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  -webkit-appearance: none;

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const StyledComboboxList = styled(ComboboxList)`
  max-height: 200px;
  overflow-y: auto;
`;

const AddressConnector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0;
`;

const ConnectorLine = styled.div`
  width: 2px;
  height: 10px;
  background: #e0e0e0;
`;

const ConnectorDot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e8f5e9;
  border-radius: 50%;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;

  ${mobile({ flexDirection: "column" })}
`;

const FormGroup = styled.div`
  flex: 1;
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${(props) => (props.$hasError ? "#f44336" : "#e0e0e0")};
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  resize: none;
  box-sizing: border-box;

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const FragileToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const FragileLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #333;
`;

const ToggleSwitch = styled.div`
  width: 48px;
  height: 26px;
  background: ${(props) => (props.$active ? "#4caf50" : "#e0e0e0")};
  border-radius: 13px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
`;

const ToggleThumb = styled.div`
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: ${(props) => (props.$active ? "24px" : "2px")};
  transition: left 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ErrorText = styled.span`
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 24px;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StepActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;

  ${mobile({ flexDirection: "column" })}
`;

const PrimaryStepButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
`;

const SecondaryStepButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #f3f6f3;
  color: #2f3a2f;
  border: 1px solid #d4ddd4;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const RouteInfoCard = styled.div`
  display: flex;
  justify-content: space-around;
  background: linear-gradient(135deg, #00566bff 0%, #014050ff 100%);
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
  flex: none;
`;

const RouteInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const RouteInfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RouteInfoValue = styled.span`
  color: white;
  font-size: 18px;
  font-weight: 700;
`;

const RouteInfoLabel = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
`;

const RouteInfoDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.3);
`;

const InfoContent = styled.div`
  padding: 4px;
`;

const InfoTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #333;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #666;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const OrderSummary = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: ${(props) => (props.$total ? "#333" : "#666")};
  font-weight: ${(props) => (props.$total ? "700" : "400")};
  font-size: ${(props) => (props.$total ? "16px" : "14px")};
  border-top: ${(props) => (props.$total ? "1px solid #ddd" : "none")};
  margin-top: ${(props) => (props.$total ? "8px" : "0")};
  padding-top: ${(props) => (props.$total ? "12px" : "8px")};
`;

const PaymentMethods = styled.div`
  margin-bottom: 20px;
`;

const PaymentMethodTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const PaymentOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid ${(props) => (props.$selected ? "#4caf50" : "#e0e0e0")};
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${(props) =>
    props.$selected ? "rgba(76, 175, 80, 0.1)" : "white"};
  transition: all 0.2s;

  &:hover {
    border-color: #4caf50;
  }
`;

const PaymentIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${(props) => props.$color}15;
  border-radius: 10px;
`;

const PaymentName = styled.span`
  font-weight: 500;
  color: #333;
`;

const SelectedPaymentTick = styled.span`
  margin-left: auto;
  color: #2e7d32;
  display: flex;
  align-items: center;
`;

const PayButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;
